'use client';
import React from 'react';
import { ProductReviews as SharedProductReviews } from '@launchstore/shared-puck';
import type {
  ProductReviewsProps, FetchReviews, CreateReviewFn, VoteOnReview,
  UploadReviewMedia, FetchCustomer,
} from '@launchstore/shared-puck';
import { useProduct } from '@/lib/hooks/useProduct';
import { useParams } from 'next/navigation';
import { getMedusaBackendUrl } from '@/lib/config';
import {
  fetchProductReviews, createReview, voteReview, uploadReviewMedia,
  ReviewsResponse, Review, CreateReviewPayload,
} from '@/lib/data/reviews';
import type { ProductData } from '@launchstore/shared-puck';

// Adapt Medusa review types → shared types
const adaptReviews = async (productId: string, params: any): Promise<import('@launchstore/shared-puck').SharedReviewsResponse> => {
  const data: ReviewsResponse = await fetchProductReviews(productId, params);
  return {
    reviews: (data.reviews || []) as unknown as import('@launchstore/shared-puck').SharedReview[],
    count: data.count || 0,
    limit: data.limit || 0,
    offset: data.offset || 0,
    average_rating: data.average_rating || 0,
    rating_distribution: data.rating_distribution || { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
    recommendation_rate: data.recommendation_rate || 0,
  };
};

const adaptCreateReview: CreateReviewFn = async (input: import('@launchstore/shared-puck').SharedCreateReviewInput) => {
  const payload: CreateReviewPayload = {
    product_id: input.product_id,
    content: input.content,
    rating: input.rating,
    first_name: input.first_name,
    last_name: input.last_name,
    title: input.title,
    pros: input.pros,
    cons: input.cons,
    recommend: input.recommend,
    media_urls: input.media_urls,
    verified_purchase: input.verified_purchase,
    verified_source: input.verified_source,
  };
  const review: Review = await createReview(payload);
  return review as unknown as import('@launchstore/shared-puck').SharedReview;
};

const voteAdapter: VoteOnReview = async (reviewId, isHelpful) => { await voteReview(reviewId, isHelpful); };
const uploadAdapter: UploadReviewMedia = async (reviewId, file) => uploadReviewMedia(reviewId, file);
const customerAdapter: FetchCustomer = async () => {
  try {
    const res = await fetch(`${getMedusaBackendUrl()}/store/customers/me`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.customer ?? null;
  } catch { return null; }
};

const SharedProductReviewsRender = (SharedProductReviews as any).render;

const ProductReviewsImpl: React.FC<ProductReviewsProps> = (props: any) => {
  const { product } = useProduct();
  const params = useParams();
  const countryCode = (params?.countryCode as string) || 'us';
  const projected: ProductData | null = product ? { id: product.id, title: product.title } : null;
  const signInHref = `/${countryCode}/account`;
  return (
    <SharedProductReviewsRender
      {...(props as any)}
      product={projected}
      fetchReviews={adaptReviews as FetchReviews}
      createReview={adaptCreateReview}
      voteOnReview={voteAdapter}
      uploadReviewMedia={uploadAdapter}
      fetchCustomer={customerAdapter}
      signInHref={signInHref}
    />
  );
};

export const ProductReviews: any = { ...SharedProductReviews, render: (props: any) => <ProductReviewsImpl {...props} /> };
export default ProductReviews;
